import type { Components, Theme } from '@mui/material/styles';

import { SHAPE, TEXT_SIZE, WEIGHT } from './scales';

/** The kit navigation: tabs, steps and their marks, the nav drawer, its items and group headings. */
export const NAVIGATION: Components<Theme> = {
  MuiTabs: {
    styleOverrides: {
      root: ({ theme }) => ({ minHeight: 40, borderBottom: `1px solid ${theme.vars.palette.divider}` }),
      indicator: ({ theme }) => theme.applyStyles('dark', { backgroundColor: theme.vars.palette.text.brand }),
    },
  },
  MuiTab: {
    styleOverrides: {
      root: ({ theme }) => ({
        minHeight: 40,
        minWidth: 0,
        padding: '8px 16px',
        color: theme.vars.palette.text.secondary,
        '&:hover': { color: theme.vars.palette.grey[700] },
        '&.Mui-selected': {
          color: theme.vars.palette.primary.dark,
          ...theme.applyStyles('dark', { color: theme.vars.palette.text.brand }),
        },
      }),
      fullWidth: ({ theme }) => ({ paddingInline: 4, fontSize: theme.typography.caption.fontSize }),
    },
  },
  MuiStep: {
    styleOverrides: { horizontal: { flexShrink: 0, paddingInline: 0 } },
  },
  MuiStepConnector: {
    styleOverrides: {
      horizontal: { flex: '0 0 24px', marginInline: 8 },
      line: ({ theme }) => ({ borderColor: theme.vars.palette.divider }),
    },
  },
  MuiStepButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        margin: 0,
        padding: '8px 12px',
        borderRadius: SHAPE.small,
        '&:hover': { backgroundColor: theme.vars.palette.action.hover },
      }),
    },
  },
  MuiStepLabel: {
    styleOverrides: {
      label: ({ theme }) => ({
        ...theme.typography.subtitle2,
        color: theme.vars.palette.grey[700],
        '&.Mui-active, &.Mui-completed': { fontWeight: WEIGHT.bold },
        '&.Mui-active': { color: theme.vars.palette.text.heading },
        '&.Mui-disabled': { color: theme.vars.palette.text.disabled },
      }),
    },
  },
  KitStepMark: {
    styleOverrides: {
      root: ({ theme }) => ({
        ...theme.typography.label,
        display: 'flex',
        width: 22,
        height: 22,
        flexShrink: 0,
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: SHAPE.full,
        color: theme.vars.palette.grey[600],
        backgroundColor: theme.vars.palette.divider,
      }),
    },
    variants: [
      {
        props: { state: 'skipped', active: false },
        style: ({ theme }) => ({
          color: theme.vars.palette.text.secondary,
          backgroundColor: 'transparent',
          border: `1px dashed ${theme.vars.palette.grey[400]}`,
        }),
      },
      {
        props: { state: 'complete' },
        style: ({ theme }) => ({
          color: theme.vars.palette.success.contrastText,
          backgroundColor: theme.vars.palette.success.main,
        }),
      },
      {
        props: { active: true },
        style: ({ theme }) => ({
          color: theme.vars.palette.primary.contrastText,
          backgroundColor: theme.vars.palette.primary.main,
        }),
      },
    ],
  },
  MuiDrawer: {
    styleOverrides: { paper: { overflowX: 'hidden' } },
  },
  KitNavItem: {
    styleOverrides: {
      root: ({ theme }) => ({
        flexShrink: 0,
        flexGrow: 0,
        gap: 10,
        padding: '8px 12px',
        fontSize: TEXT_SIZE.control,
        whiteSpace: 'nowrap',
        color: theme.vars.palette.text.primary,
        '&[aria-current="page"]': {
          fontWeight: WEIGHT.bold,
          color: theme.vars.palette.tint.brand.text,
          backgroundColor: theme.vars.palette.tint.brand.fill,
        },
      }),
    },
    variants: [
      {
        props: { rail: true },
        style: ({ theme }) => ({
          justifyContent: 'center',
          paddingInline: 0,
          '&[aria-current="page"]': { boxShadow: `inset 3px 0 0 ${theme.vars.palette.primary.main}` },
        }),
      },
      {
        props: { onSurface: true },
        style: ({ theme }) => ({
          '&:not([aria-current="page"]):hover': {
            backgroundColor: theme.vars.palette.background.paper,
            ...theme.applyStyles('dark', { backgroundColor: theme.vars.palette.divider }),
          },
        }),
      },
    ],
  },
  KitNavGroup: {
    styleOverrides: {
      root: ({ theme }) => ({
        ...theme.typography.overline,
        letterSpacing: '0.12em',
        color: theme.vars.palette.text.secondary,
        '&:hover': { color: theme.vars.palette.grey[600] },
      }),
    },
  },
};
